import "reflect-metadata";
import { LitElement, html } from "lit";
import { property, state, customElement } from "lit/decorators.js";
import { FooterService } from "./services/FooterService";
import { ConfigService } from "./services/config/ConfigService";
import { CategoryDataService } from "./services/CategoryDataService";
import { MparticleService } from "./services/mParticleService";
import { KevelCustomEvents, RMDataService, StickyBannerData } from "./services/RMDataService";
import { ELEMENT_POSITION, EVENT_NAME, EVENT_TYPE } from "@cash-rewards/mparticlejs";
import { HttpService } from "./services/HttpService";
import { JwtAuthService } from "./services/JwtAuthService";
import { inject, container } from "inversify-props";
import { Constructor } from "inversify-props/dist/lib/inversify.types";
import * as customStyles from "./styles.css?inline";
import "./components/FooterLinks";
import "./components/FooterSocials";
import "./components/Featured";
import "./components/MarqueeBanner";
import "./components/Trustpilot";
import "./components/StarlightBanner";
import "./components/StickyBanner";
import { getPartnerStore } from "./common/getPartnerStore";

function bindIfNotBound<T>(cons: Constructor<T>, identifier: string): void {
  if (!container.isBound(identifier)) {
    container.addSingleton(cons, identifier);
  }
}
bindIfNotBound<FooterService>(FooterService, "FooterService");
bindIfNotBound<ConfigService>(ConfigService, "ConfigService");
bindIfNotBound<HttpService>(HttpService, "HttpService");
bindIfNotBound<CategoryDataService>(CategoryDataService, "CategoryDataService");
bindIfNotBound<RMDataService>(RMDataService, "RMDataService");
bindIfNotBound<JwtAuthService>(JwtAuthService, "JwtAuthService");
bindIfNotBound<MparticleService>(MparticleService, "MparticleService");
@customElement("cr-global-footer")
export class CrGlobalFooter extends LitElement {
  @inject("FooterService") footerService: FooterService;
  @inject("ConfigService") configService: ConfigService;
  @inject("CategoryDataService") categoryDataService: CategoryDataService;
  @inject("RMDataService") RMDataService: RMDataService;
  @inject("JwtAuthService") jwtAuthService: JwtAuthService;
  @inject("MparticleService") mParticleService: MparticleService;

  constructor() {
    super();
    this.getPartner();
  }

  readonly SCRIPT_TAG_ID = "cr-global-footer-style";
  readonly TRUST_PILOT_URL =
    "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
  readonly TRUST_PILOT_TYPE = "text/javascript";
  readonly TRUST_PILOT_ID = "trustwidget";

  @property()
  showFeatured: string | boolean = false;

  @property()
  isMarketing: string | boolean = false;

  @property()
  showBorderTop: string | boolean = false;

  @property()
  showMarquee: string | boolean = false;

  @property()
  showTrustPilot: string | boolean = false;

  @property()
  showStickyBanner: string | boolean = false;

  @property()
  theme: string = "white";

  @property({
    type: Boolean,
    converter: (value: string) => {
      return value === "true";
    },
  })
  showStarlightBanner: string | boolean = false;

  @property()
  stickyBannerCategory?: string;

  @state()
  configLoaded = false;

  @state()
  isLoggedIn = false;

  @state()
  links = [];

  @state()
  categories: any = [];

  @state()
  appLinks: any = [];

  @state()
  featured: any = [];

  @state()
  starlightInfo: any = [];

  @state()
  stickyBannerData?: StickyBannerData;

  @state()
  hideStickyBanner: boolean = false;

  @state()
  stickyBannerElementId: string = "sticky-banner";

  @state()
  partner: string | null = null;

  async loadConfig() {
    try {
      this.configLoaded = await this.configService.load();
    } catch (error) {
      this.configLoaded = false;
    }
  }

  override async connectedCallback() {
    super.connectedCallback();
    this.injectCSS();
    this.convertProps();
    await this.loadConfig();

    this.mParticleService.init();

    this.loadTrustPilot();

    if (this.configLoaded) {
      await this.refreshData();
    }

    this.trackImpression();
  }

  async refreshData() {
    const footerData = await this.footerService.getFooterList();
    this.isLoggedIn = await this.jwtAuthService.isAuthenticated();
    this.categories = await this.categoryDataService.getCategoryList();
    this.featured = footerData.data.attributes.featured;
    this.appLinks = footerData.data.attributes.tools.appLinks;
    this.starlightInfo = footerData.data.attributes.starlight;
    this.links = footerData.data.attributes.groups;

    if (this.showStickyBanner) {
      this.hideStickyBanner =
        sessionStorage.getItem("hideStickyBanner") === "true" || false;
      const stickyBannerData = await this.RMDataService.getStickyBannerData(
        this.stickyBannerCategory
      );

      if (stickyBannerData?.length) {
        this.stickyBannerData = stickyBannerData[0];
      }

      this.stickyBannerElementId = this.stickyBannerCategory ? `sticky-banner-${this.stickyBannerCategory}` : "sticky-banner"
    }
  }

  override createRenderRoot() {
    return this;
  }

  private convertProps() {
    const properties = [
      "showFeatured",
      "isMarketing",
      "showMarquee",
      "showStickyBanner",
      "showBorderTop",
      "showTrustPilot",
      "showStarlightBanner",
    ];

    properties.forEach((property: string) => {
      if (typeof this[property] === "string") {
        this[property] = JSON.parse(this[property]);
      }
    });
  }

  private loadTrustPilot() {
    const script = document.createElement("script");
    script.type = this.TRUST_PILOT_TYPE;
    script.src = this.TRUST_PILOT_URL;
    script.async = true;
    document.head.appendChild(script);
    const trustwidget = document.getElementById(this.TRUST_PILOT_ID);
    window?.Trustpilot?.loadFromElement(trustwidget);
  }

  injectCSS() {
    if (
      typeof document != "undefined" &&
      !document.getElementById(this.SCRIPT_TAG_ID)
    ) {
      const elementStyle = document.createElement("style");
      elementStyle.id = this.SCRIPT_TAG_ID;
      elementStyle.appendChild(document.createTextNode(customStyles.default));
      document.head.appendChild(elementStyle);
    }
  }

  override async disconnectedCallback() {
    const footerCSS = document.getElementById(this.SCRIPT_TAG_ID);
    footerCSS?.remove();


    this.mParticleService.disconnect();
  }

  private displayedFeatured = () => {
    if (this.isMarketing || (this.showFeatured && !this.isLoggedIn)) {
      return html`
        <featured-links .featuredData=${this.featured}></featured-links>
      `;
    }
    return html``;
  };

  private triggerKevelTracking = (eventId: KevelCustomEvents) => {
    const eventImage = this.stickyBannerData?.eventUrls[eventId];

    if(eventImage) {
      const img = new Image();
      img.src = eventImage;
    }
  }

  private closestickybanner = () => {
    sessionStorage.setItem("hideStickyBanner", "true");
    this.hideStickyBanner = true;
    this.triggerKevelTracking(KevelCustomEvents.CLOSE_AD);

    if(this.stickyBannerData) {

      this.mParticleService.trackEvent(
        EVENT_NAME.dismiss_ad_unit, 
        EVENT_TYPE.Other,
        this.mParticleService.getMpClickEventData(this.stickyBannerElementId, ELEMENT_POSITION.footer, 6650, this.stickyBannerData)
      );
    }
  };

  private onClickStickyBanner = () => {
    if(this.stickyBannerData) {

      this.mParticleService.trackEvent(
        EVENT_NAME.click_ad_unit, 
        EVENT_TYPE.Other,
        this.mParticleService.getMpClickEventData(this.stickyBannerElementId, ELEMENT_POSITION.footer, 6650, this.stickyBannerData)
      );
    }
  }

  private trackImpression() {
    const adElement: HTMLElement | null = document.querySelector('standalone-stickybanner');
    const ad_size = 6650;
    const content_descriptor = `Sticky banner ${this.stickyBannerCategory ?? ''}`;

    if(this.stickyBannerData) {
      this.mParticleService.trackImpression(adElement, this.stickyBannerElementId, ad_size, content_descriptor, this.stickyBannerData);
    }
  }

  private displaystickybanner = () => {
    if (
      this.showStickyBanner &&
      this.stickyBannerData &&
      !this.hideStickyBanner
    ) {
      // Inline styles to stop impression from being triggered by mistake as CSS loads and because content inside is on isolation mode 
      return html`<standalone-stickybanner style="height: 45px; width: 100%; display: flex; position: fixed; bottom:0; left:0; z-index: 10;"
        .text="${this.stickyBannerData.text}"
        .actionText="${this.stickyBannerData.cta}"
        .href="${this.stickyBannerData.clickUrl}"
        .variant="${this.stickyBannerData.variant}"
        .trackingPixelUrl="${this.stickyBannerData.impressionUrl}"
        .onClose="${this.closestickybanner}"
        .onClick="${this.onClickStickyBanner}"
      ></standalone-stickybanner>`;
    }
    return html``;
  };

  private displayMarquee = () => {
    if (this.showMarquee) {
      return html`<marquee-banner></marquee-banner> `;
    }
    return html``;
  };

  private displayTrustPilot = () => {
    if (this.isMarketing || (this.showTrustPilot && !this.isLoggedIn)) {
      return html`<trust-pilot></trust-pilot>`;
    }
    return html``;
  };

  private getPartner = () => {
    const partnerStore = getPartnerStore();
    if (partnerStore?.partner) {
      this.partner = partnerStore.partner;
    }
  };

  override render() {
    return html`
      ${this.displaystickybanner()}
      <div id="cr-footer"></div>
      <div id="gfooter">
        <div
          class="${this.showBorderTop &&
          this.isLoggedIn &&
          "twf-border-[#BDBDBD] twf-border-t-[1px] twf-border-solid "}"
        >
          ${this.displayTrustPilot()} ${this.displayedFeatured()}
          ${this.isMarketing || (this.showStarlightBanner && !this.isLoggedIn)
            ? html`<starlight-banner .starlightInfo=${this.starlightInfo}></starlight-banner>`
            : html``}
          ${this.displayMarquee()}
          <div
            class="${this.theme === "purple"
              ? "twf-bg-[#330066]"
              : "twf-bg-white"}"
          >
            <footer-links
              .theme=${this.theme}
              .appLinks=${this.appLinks}
              .categories=${this.categories}
              .links=${this.links}
              .isLoggedIn=${this.isLoggedIn}
              .partner=${this.partner}
            >
            </footer-links>
            <footer-socials .theme=${this.theme}></footer-socials>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cr-global-footer": CrGlobalFooter;
  }
  interface Window {
    Trustpilot: {
      loadFromElement: any;
    };
  }
}
